// Intestazione del negozio personale: barra di fiducia (co-branding Rarifex discreto),
// logo, nome e menu della bottega. Di Rarifex restano anche account e carrello.
// Nel modello Classico, in home, l'intestazione è trasparente sopra la copertina
// e diventa piena scorrendo.
import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, ShieldCheck, ShoppingBag, Truck, User, X, Headphones } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { optimizeImageUrl } from '../utils/imageUrl';
import { useShop } from './ShopContext';
import { RarifexMark } from './ShopUi';

const useCartCount = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const update = () => {
      try { setCount(JSON.parse(localStorage.getItem('rarifex_cart') || '[]').length); } catch (_) { setCount(0); }
    };
    update();
    window.addEventListener('cartUpdated', update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener('cartUpdated', update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return count;
};

const useScrolled = (threshold = 40) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
};

// Barra sottile in cima: la bottega è su Rarifex, con le garanzie della piattaforma
function TrustBar() {
  return (
    <div className="bg-shop-primary-dark text-white/90 text-[11px] md:text-xs" data-testid="shop-trust-bar">
      <div className="max-w-6xl mx-auto px-4 h-8 flex items-center justify-center md:justify-between gap-4">
        <span className="inline-flex items-center gap-1.5">
          Bottega artigiana su <RarifexMark />
        </span>
        <span className="hidden md:flex items-center gap-5 text-white/80">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Pagamento protetto</span>
          <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Spedizione tracciata</span>
          <span className="inline-flex items-center gap-1.5"><Headphones className="w-3.5 h-3.5" /> Assistenza sugli ordini</span>
        </span>
      </div>
    </div>
  );
}

export default function ShopHeader() {
  const shop = useShop();
  const { user } = useAuth();
  const location = useLocation();
  const cartCount = useCartCount();
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const { style, branding, name, navItems, paths, crafter, embedded } = shop;

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isHome = !embedded && location.pathname.replace(/\/$/, '') === paths.home;
  const hasCover = !!(crafter.cover_image || crafter.cover_images?.[0]);
  // Trasparente sopra la copertina solo nel modello Classico, in home, finché non si scorre
  const fixedMode = style.transparentHeader && isHome && hasCover;
  const overlay = fixedMode && !scrolled && !open;

  const logo = branding.logo_url || crafter.avatar_url;
  const headerTone = overlay ? 'bg-transparent text-white' : style.header;
  const linkTone = overlay ? 'text-white/85 hover:text-white' : style.headerLink;
  const linkActive = overlay ? 'text-white border-b-2 border-white' : style.headerLinkActive;

  const brand = (
    <Link to={paths.home} className={`flex items-center gap-3 min-w-0 ${style.centeredHeader ? 'flex-col gap-2' : ''}`} data-testid="shop-header-brand">
      {logo ? (
        <img
          src={optimizeImageUrl(logo, 160)}
          alt={`Logo di ${name}`}
          className={`${style.centeredHeader ? 'h-14 w-14' : 'h-11 w-11'} rounded-lg object-contain bg-white p-1 shrink-0 ${style.id === 'galleria' || overlay ? '' : 'border border-slate-200'}`}
        />
      ) : (
        <span className="h-11 w-11 rounded-lg bg-shop-primary text-white font-bold flex items-center justify-center shrink-0">
          {name[0].toUpperCase()}
        </span>
      )}
      <span className={`${style.heading} ${style.centeredHeader ? 'text-xl' : 'text-lg md:text-xl'} truncate`}>{name}</span>
    </Link>
  );

  const linkClass = ({ isActive }) =>
    `py-1 text-sm font-medium transition-colors whitespace-nowrap ${isActive ? linkActive : linkTone}`;

  const icons = (
    <div className="flex items-center gap-1">
      <Link
        to={user ? '/dashboard' : '/login'}
        className="p-2 rounded-full hover:bg-black/10"
        aria-label={user ? 'Il tuo account Rarifex' : 'Accedi'}
        title={user ? 'Il tuo account Rarifex: ordini, messaggi e preferiti' : 'Accedi o registrati con Rarifex'}
      >
        <User className="w-5 h-5" />
      </Link>
      <Link to="/cart" className="relative p-2 rounded-full hover:bg-black/10" aria-label="Carrello" data-testid="shop-cart">
        <ShoppingBag className="w-5 h-5" />
        {cartCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-shop-accent text-white text-[10px] font-bold flex items-center justify-center">
            {cartCount > 99 ? '99+' : cartCount}
          </span>
        )}
      </Link>
      <button type="button" className="md:hidden p-2 rounded-full hover:bg-black/10" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open}>
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
    </div>
  );

  return (
    <div className={`${fixedMode ? 'fixed' : 'sticky'} top-0 inset-x-0 z-40`} data-testid="shop-header-wrap">
      <TrustBar />
      <header className={`transition-colors duration-300 ${headerTone} ${scrolled && !overlay ? 'shadow-sm' : ''}`} data-testid="shop-header">
        {style.centeredHeader ? (
          <div className="max-w-6xl mx-auto px-4 pt-5 pb-3">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center">
              <span />
              {brand}
              <div className="justify-self-end">{icons}</div>
            </div>
            <nav className="hidden md:flex justify-center gap-8 mt-4" aria-label="Menu della bottega">
              {navItems.map((n) => <NavLink key={n.key} to={n.to} className={linkClass}>{n.label}</NavLink>)}
            </nav>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-4 h-16 md:h-20 flex items-center justify-between gap-4">
            {brand}
            <nav className="hidden md:flex items-center gap-6" aria-label="Menu della bottega">
              {navItems.map((n) => <NavLink key={n.key} to={n.to} className={linkClass}>{n.label}</NavLink>)}
            </nav>
            {icons}
          </div>
        )}
        {open && (
          <nav className="md:hidden border-t border-black/10 px-4 py-3 flex flex-col gap-1" aria-label="Menu della bottega">
            <NavLink to={paths.home} end className={({ isActive }) => `py-2 text-base ${isActive ? 'font-semibold' : 'opacity-80'}`}>Home</NavLink>
            {navItems.map((n) => (
              <NavLink key={n.key} to={n.to} className={({ isActive }) => `py-2 text-base ${isActive ? 'font-semibold' : 'opacity-80'}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
    </div>
  );
}
